import { UnwrapError } from '../unwrap-error';
/**
 * Abstract base class for result types (Ok and Err).
 * Provides a common interface for success and error results with type-safe operations.
 *
 * @template T - Boolean literal type indicating success (true) or failure (false)
 */
export class Result {
    constructor(success) {
        this.success = success;
    }
    /**
     * Checks if this result represents a success.
     *
     * @returns True if successful, false otherwise
     */
    isOk() {
        return (this.success === true);
    }
    /**
     * Checks if this result represents an error.
     *
     * @returns True if error, false otherwise
     */
    isErr() {
        return (this.success === false);
    }
}
/**
 * Represents a successful result containing data.
 * This is returned by operations that complete successfully.
 *
 * @template T - Type of the data contained in the success result
 *
 * @example
 * ```ts
 * import { ok } from 'tryless';
 * const result = ok(42);
 * // result.success === true
 * // result.data === 42
 * ```
 */
export class Ok extends Result {
    /**
     * Creates a new Ok instance.
     *
     * @param data - The data to wrap in the success result
     */
    constructor(data) {
        super(true);
        this.data = data;
    }
    /**
     * Returns the wrapped data.
     * This operation is safe because Ok always contains data.
     *
     * @returns The data contained in this result
     */
    unwrap() {
        return this.data;
    }
    /**
     * Returns the wrapped data.
     * The default value is ignored for Ok results.
     *
     * @template U - Type of the default value (unused)
     * @returns The data contained in this result
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    unwrapOr(_defaultValue) {
        return this.data;
    }
    /**
     * Returns the wrapped data.
     * The default value function is not called for Ok results.
     *
     * @template U - Type of the computed value (unused)
     * @returns The data contained in this result
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    unwrapOrElse(_defaultValue) {
        return this.data;
    }
    /**
     * Attempts to unwrap as an error, but always throws because this is a success result.
     *
     * @param customError - Optional custom error message
     * @throws {UnwrapError} Always throws because Ok is not an error
     */
    unwrapErr(customError) {
        throw new UnwrapError(this, this.unwrapErr, `Could not unwrap error`, customError);
    }
    /**
     * Returns the default value because this is not an error result.
     *
     * @template U - Type of the default value
     * @param defaultValue - Value to return
     * @returns The default value
     */
    unwrapErrOr(defaultValue) {
        return defaultValue;
    }
    /**
     * Computes and returns a default value because this is not an error result.
     *
     * @template U - Type of the computed value
     * @param defaultValue - Function to compute the default value
     * @returns The computed default value
     */
    unwrapErrOrElse(defaultValue) {
        return defaultValue(this);
    }
    /**
     * Asserts that this result is an error with a specific type.
     * Always throws because Ok is a success result.
     *
     * @template E - Expected error type
     * @param error - Expected error string
     * @param customError - Optional custom error message
     * @throws {UnwrapError} Always throws because Ok is not an error
     */
    expect(error, customError) {
        throw new UnwrapError(this, this.expect, `Expected error ${error}, but got success`, customError);
    }
    /**
     * Returns the given result because this is a success.
     * Implements the logical AND operation.
     *
     * @template R - Type of the result to return
     * @param result - Result to return
     * @returns The given result
     */
    and(result) {
        return result;
    }
    /**
     * Applies the function to the wrapped data.
     * Enables chaining of operations on successful results.
     *
     * @template U - Type of result returned by the function
     * @param fn - Function to apply to the data
     * @returns Result from the function
     */
    andThen(fn) {
        return fn(this.data);
    }
    /**
     * Returns this result because it's already successful.
     * The alternative result is ignored.
     *
     * @template R - Type of the alternative result (unused)
     * @returns This result
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    or(_result) {
        return this;
    }
    /**
     * Returns this result because it's already successful.
     * The recovery function is not called.
     *
     * @template R - Type of result from recovery (unused)
     * @returns This result
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    orElse(_fn) {
        return this;
    }
    /**
     * Applies a transformation function to this result.
     *
     * @template U - Return type of the transformation
     * @param fn - Function to transform the result
     * @returns The transformed value
     */
    map(fn) {
        return fn(this);
    }
    /**
     * Returns a string representation of this success result.
     *
     * @returns String in format "Success<data>"
     */
    toString() {
        return `Success<${this.data}>`;
    }
}
/**
 * Represents a failed result containing an error and optional reason.
 * This is returned by operations that fail or encounter errors.
 *
 * @template E - String literal type representing the error identifier
 * @template R - Type of the reason/additional error information
 *
 * @example
 * ```ts
 * import { err } from 'tryless';
 * const result = err('NotFound', 'User not found');
 * // result.success === false
 * // result.error === 'NotFound'
 * // result.reason === 'User not found'
 * ```
 */
export class Err extends Result {
    /**
     * Creates a new Err instance.
     * Automatically captures a stack trace if available.
     *
     * @param error - The error identifier string
     * @param reason - Additional information about the error
     * @param caller - Optional function to exclude from stack trace
     */
    constructor(error, reason, caller) {
        super(false);
        if ("captureStackTrace" in Error && typeof Error.captureStackTrace === 'function') {
            Error.captureStackTrace(this, caller || this.constructor);
        }
        this.error = error;
        this.reason = reason;
    }
    /**
     * Attempts to unwrap the result, but always throws because this is an error.
     *
     * @param customError - Optional custom error message
     * @throws {UnwrapError} Always throws because Err contains no data
     */
    unwrap(customError) {
        throw new UnwrapError(this, this.unwrap, `Could not unwrap error`, customError);
    }
    /**
     * Returns the default value because this is an error result.
     *
     * @template U - Type of the default value
     * @param defaultValue - Value to return
     * @returns The default value
     */
    unwrapOr(defaultValue) {
        return defaultValue;
    }
    /**
     * Computes and returns a default value using this error result.
     *
     * @template U - Type of the computed value
     * @param defaultValue - Function to compute the default value from this error
     * @returns The computed default value
     */
    unwrapOrElse(defaultValue) {
        return defaultValue(this);
    }
    /**
     * Returns this error result.
     * This operation is safe because Err always contains an error.
     *
     * @returns This error result
     */
    unwrapErr() {
        return this;
    }
    /**
     * Returns this error result.
     * The default value is ignored for Err results.
     *
     * @template U - Type of the default value (unused)
     * @returns This error result
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    unwrapErrOr(_defaultValue) {
        return this;
    }
    /**
     * Returns this error result.
     * The default value function is not called for Err results.
     *
     * @template U - Type of the computed value (unused)
     * @returns This error result
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    unwrapErrOrElse(_defaultValue) {
        return this;
    }
    /**
     * Asserts that this error matches the expected error type.
     * Returns this result if it matches, throws otherwise.
     *
     * @template U - Expected error string literal type
     * @param error - Expected error string
     * @param customError - Optional custom error message
     * @returns This result if error matches
     * @throws {UnwrapError} If error doesn't match
     */
    expect(error, customError) {
        if (this.error === error) {
            return this;
        }
        throw new UnwrapError(this, this.expect, `Expected error ${error}, but got error ${this.error}`, customError);
    }
    /**
     * Returns this error because it's not a success.
     * The alternative result is ignored.
     *
     * @template R - Type of the alternative result (unused)
     * @returns This error result
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    and(_result) {
        return this;
    }
    /**
     * Returns this error because it's not a success.
     * The function is not called.
     *
     * @template U - Type of result from function (unused)
     * @returns This error result
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    andThen(_fn) {
        return this;
    }
    /**
     * Returns the given result because this is an error.
     * Implements the logical OR operation for error recovery.
     *
     * @template R - Type of the fallback result
     * @param result - Result to return as fallback
     * @returns The given fallback result
     */
    or(result) {
        return result;
    }
    /**
     * Applies the function to this error for recovery.
     * Enables error handling and recovery patterns.
     *
     * @template R - Type of result returned by the function
     * @param fn - Function to handle the error
     * @returns Result from the recovery function
     */
    orElse(fn) {
        return fn(this);
    }
    /**
     * Applies a transformation function to this error result.
     *
     * @template U - Return type of the transformation
     * @param fn - Function to transform the result
     * @returns The transformed value
     */
    map(fn) {
        return fn(this);
    }
    /**
     * Returns a string representation of this error result.
     *
     * @returns String in format "error: reason - stack"
     */
    toString() {
        return `${this.error}: ${this.reason} - ${this.stack}`;
    }
}
//# sourceMappingURL=classes.js.map